import { useCallback } from "react";
import userJobApi from "@/api/userJob";
import { Modal } from "@/components/Modal/Modal";
import { ModalFooter } from "@/components/Modal/ModalFooter";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal } from "@/store/modal/slice";
import { deleteUserJob } from "@/store/userJob/slice";
import { ModalTypes } from "@/types/modal";

interface ConfirmationModalProps {
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;

    // These props are used to trigger on confirm actions
    mode?: "delete" | "close";
    userJobId?: string;
}

export const ConfirmationModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.CONFIRMATION));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.CONFIRMATION),
    ) as ConfirmationModalProps;
    const dispatch = useAppDispatch();

    const handleCancel = useCallback(() => {
        dispatch(closeModal(ModalTypes.CONFIRMATION));
    }, [dispatch]);

    const onConfirm = useCallback(async () => {
        if (modalProps?.mode === "delete" && modalProps?.userJobId) {
            await userJobApi.deleteUserJob(modalProps.userJobId);
            dispatch(deleteUserJob(modalProps.userJobId));
            dispatch(closeModal(ModalTypes.CONFIRMATION));
        } else if (modalProps?.mode === "close") {
            dispatch(closeModal(ModalTypes.CONFIRMATION));
        }
    }, [dispatch, modalProps]);

    return (
        <Modal
            modalType={ModalTypes.CONFIRMATION}
            open={isOpen}
            title={modalProps?.title}
            width="md"
            footer={
                <ModalFooter
                    primaryActionText={modalProps?.confirmLabel || "Confirm"}
                    primaryActionOnClick={onConfirm}
                    secondaryActionText={modalProps?.cancelLabel || "Cancel"}
                    secondaryActionOnClick={handleCancel}
                    primaryActionColor="red"
                />
            }
        >
            <div className="flex flex-col gap-6">
                <AppText
                    variant="regular"
                    className="text-neutral-700"
                >
                    {modalProps?.message}
                </AppText>
            </div>
        </Modal>
    );
};
